/**
 * A list of switchable items on a card: each row is a switch, a title, a
 * one-line description and, while the row is on, its settings next to it.
 * The objectives, the rejection rules and the ranking of the Optimization
 * card are such lists.
 *
 * items: [{ key, title, description, enabled, onToggle(next), fields, note, columns }]
 * `fields` are SettingFields specs into `config` (shown while the row is
 * on); `note` is a caption under them. The invalid entries of every row are
 * summed and reported through `onInvalidChange`.
 */
import React from "react";
import { Box, Chip, Stack, Switch, Typography } from "@mui/material";
import SettingFields, { useInvalidTotal } from "./SettingFields";
import { HAIRLINE, INK } from "../../../components/cardChrome";

const ACCENT = "#1e4fa3";

export default function ToggleRows({
  title,
  hint,
  caption = "",
  items,
  config,
  onChange,
  onInvalidChange,
  testPrefix,
  rowsTestId = `${testPrefix}-rows`,
}) {
  const setterFor = useInvalidTotal(onInvalidChange);

  return (
    <Box
      data-testid={rowsTestId}
      sx={{ border: `1px solid ${HAIRLINE}`, borderRadius: 3, overflow: "hidden", bgcolor: "#ffffff", width: "100%" }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 2, py: 1.25, bgcolor: "#f8fafc", borderBottom: `1px solid ${HAIRLINE}` }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ display: "block", letterSpacing: 1.2, fontWeight: 700, lineHeight: 1.6, color: INK }}>
            {title}
          </Typography>
          {caption ? (
            <Typography variant="caption" sx={{ display: "block", color: "#64748b", lineHeight: 1.35 }}>
              {caption}
            </Typography>
          ) : null}
        </Box>
        {hint ? (
          <Chip
            size="small"
            label={hint}
            data-testid={`${rowsTestId}-hint`}
            sx={{ bgcolor: "#e6f0fb", color: ACCENT, fontWeight: 700, flexShrink: 0 }}
          />
        ) : null}
      </Stack>
      {items.map((item, index) => {
        const showFields = item.enabled && Array.isArray(item.fields) && item.fields.length > 0;
        return (
          <Box
            key={item.key}
            data-testid={`${testPrefix}-row-${item.key}`}
            data-enabled={String(item.enabled)}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "auto minmax(0, 1fr)", md: "auto minmax(0, 1fr) minmax(0, 1.25fr)" },
              columnGap: 2,
              rowGap: 1.25,
              alignItems: "start",
              px: 2,
              py: 1.5,
              borderTop: index === 0 ? "none" : `1px solid ${HAIRLINE}`,
              borderLeft: `3px solid ${item.enabled ? ACCENT : "transparent"}`,
              bgcolor: item.enabled ? "#ffffff" : "#fbfcfd",
              transition: "background-color 0.15s ease, border-color 0.15s ease",
            }}
          >
            <Switch
              checked={Boolean(item.enabled)}
              onChange={(event) => item.onToggle(event.target.checked)}
              size="small"
              sx={{ mt: 0.25 }}
              inputProps={{ "data-testid": `${testPrefix}-${item.key}-switch`, "aria-label": item.title }}
            />
            <Box sx={{ minWidth: 0, pt: 0.5 }}>
              <Typography sx={{ fontWeight: 600, lineHeight: 1.3, color: item.enabled ? INK : "#64748b" }}>
                {item.title}
              </Typography>
              {item.description ? (
                <Typography variant="caption" sx={{ display: "block", color: "#64748b", lineHeight: 1.4, mt: 0.25 }}>
                  {item.description}
                </Typography>
              ) : null}
            </Box>
            {showFields ? (
              <Box sx={{ gridColumn: { xs: "1 / -1", md: "3" }, minWidth: 0 }}>
                <SettingFields
                  config={config}
                  fields={item.fields}
                  onChange={onChange}
                  onInvalidChange={setterFor(item.key)}
                  testPrefix={testPrefix}
                  columns={item.columns || 2}
                />
                {item.note ? (
                  <Typography variant="caption" sx={{ display: "block", color: "#64748b", mt: 0.5 }}>
                    {item.note}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}
